import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import './UserManagement.css';

const ROLES = ['athlete', 'parent', 'coach', 'admin'];
const ROLE_COLORS = { admin: '#8e44ad', coach: '#1a5276', parent: '#27ae60', athlete: '#e67e22', superadmin: '#c0392b', staff: '#7f8c8d' };

const BLANK_FORM = { name: '', email: '', password: '', role: 'coach', phone: '' };

function UserManagement() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [search, setSearch] = useState('');
  const [pendingCoaches, setPendingCoaches] = useState([]);
  const [activeSection, setActiveSection] = useState('users');
  const [actionLoading, setActionLoading] = useState(null);

  // Add User form state
  const [addForm, setAddForm] = useState(BLANK_FORM);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  let currentUser = null;
  try { currentUser = JSON.parse(localStorage.getItem('user')); } catch {}

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`${API_BASE_URL}/api/admin/users`);
      setUsers(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPendingCoaches = useCallback(async () => {
    try {
      const { data } = await api.get(`${API_BASE_URL}/api/admin/pending-coaches`);
      setPendingCoaches(data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchPendingCoaches();
  }, [fetchUsers, fetchPendingCoaches]);

  // Admin-driven password reset (self-service email reset isn't enabled yet).
  // Generates a temporary password, sets it, and shows it so the admin can hand
  // it to the user — who can change it after logging in.
  const resetPassword = async (userId, userName) => {
    if (!window.confirm(`Reset ${userName}'s password? You'll get a temporary password to give them.`)) return;
    const temp = 'Gym-' + Math.random().toString(36).slice(2, 10);
    setActionLoading(userId + '_pw');
    try {
      await api.put(`${API_BASE_URL}/api/admin/users/${userId}/reset-password`, { newPassword: temp });
      window.alert(`New temporary password for ${userName}:\n\n${temp}\n\nGive this to them — they can change it after logging in.`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setActionLoading(null);
    }
  };

  const changeRole = async (userId, newRole) => {
    if (!window.confirm(`Change this user's role to ${newRole}?`)) return;
    setActionLoading(userId + '_role');
    try {
      await api.put(`${API_BASE_URL}/api/admin/users/${userId}/role`, { role: newRole });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleActive = async (userId, isCurrentlyActive) => {
    const action = isCurrentlyActive ? 'deactivate' : 'activate';
    if (!window.confirm(`${isCurrentlyActive ? 'Suspend' : 'Reactivate'} this account?`)) return;
    setActionLoading(userId + '_active');
    try {
      await api.put(`${API_BASE_URL}/api/admin/users/${userId}/${action}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update user');
    } finally {
      setActionLoading(null);
    }
  };

  const approveCoach = async (coachId) => {
    setActionLoading(coachId + '_approve');
    try {
      await api.put(`${API_BASE_URL}/api/admin/approve-coach/${coachId}`);
      fetchPendingCoaches();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve coach');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddError('');
    setAddSuccess('');
    if (!addForm.name || !addForm.email || !addForm.password) {
      setAddError('Name, email, and password are required.');
      return;
    }
    setAddLoading(true);
    try {
      const { data } = await api.post(`${API_BASE_URL}/api/admin/users`, {
        name: addForm.name,
        email: addForm.email,
        password: addForm.password,
        role: addForm.role,
        phone: addForm.phone || undefined
      });
      setAddSuccess(`✓ ${data.user.name} added as ${addForm.role} — they can log in immediately.`);
      setAddForm(BLANK_FORM);
      fetchUsers();
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setAddLoading(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const roleMatch = filterRole === 'all' || u.role === filterRole;
    const searchMatch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    return roleMatch && searchMatch;
  });

  return (
    <div className="user-management-container">
      <div className="um-header">
        <h1>User Management</h1>
        {pendingCoaches.length > 0 && (
          <div className="pending-alert" onClick={() => setActiveSection('approvals')}>
            {pendingCoaches.length} coach{pendingCoaches.length > 1 ? 'es' : ''} awaiting approval
          </div>
        )}
      </div>

      <div className="um-tabs">
        <button className={`um-tab ${activeSection === 'users' ? 'active' : ''}`} onClick={() => setActiveSection('users')}>
          All Users <span className="tab-count-small">{users.length}</span>
        </button>
        <button className={`um-tab ${activeSection === 'add' ? 'active' : ''}`} onClick={() => { setActiveSection('add'); setAddError(''); setAddSuccess(''); }}>
          + Add User
        </button>
        <button className={`um-tab ${activeSection === 'approvals' ? 'active' : ''}`} onClick={() => setActiveSection('approvals')}>
          Pending Approvals
          {pendingCoaches.length > 0 && <span className="badge-dot">{pendingCoaches.length}</span>}
        </button>
      </div>

      {/* ── ADD USER ── */}
      {activeSection === 'add' && (
        <div className="add-user-section">
          <h2>Add New User</h2>
          <p className="add-user-hint">
            Users created here are <strong>immediately active</strong> — no approval or email needed.
            Share their credentials directly.
          </p>

          {addError && <div className="um-error">{addError}</div>}
          {addSuccess && <div className="um-success">{addSuccess}</div>}

          <form className="add-user-form" onSubmit={handleAddUser}>
            <div className="auf-row">
              <div className="auf-field">
                <label>Full Name *</label>
                <input type="text" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} placeholder="Jane Smith" required />
              </div>
              <div className="auf-field">
                <label>Role *</label>
                <select value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))}>
                  {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                </select>
              </div>
            </div>

            <div className="auf-row">
              <div className="auf-field">
                <label>Email *</label>
                <input type="email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} placeholder="jane@mygym.com" required />
              </div>
              <div className="auf-field">
                <label>Phone</label>
                <input type="tel" value={addForm.phone} onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))} placeholder="(555) 000-0000" />
              </div>
            </div>

            <div className="auf-row">
              <div className="auf-field auf-password">
                <label>Temporary Password *</label>
                <div className="auf-password-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={addForm.password}
                    onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Min 8 characters"
                    required
                    minLength={8}
                  />
                  <button type="button" className="auf-toggle-pw" onClick={() => setShowPassword(v => !v)}>
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
            </div>

            <div className="auf-actions">
              <button type="button" className="auf-btn secondary" onClick={() => { setAddForm(BLANK_FORM); setAddError(''); setAddSuccess(''); }}>
                Clear
              </button>
              <button type="submit" className="auf-btn primary" disabled={addLoading}>
                {addLoading ? 'Creating...' : `Add ${addForm.role.charAt(0).toUpperCase() + addForm.role.slice(1)}`}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── APPROVALS ── */}
      {activeSection === 'approvals' && (
        <div className="approvals-section">
          <h2>Coach Approvals</h2>
          {pendingCoaches.length === 0 ? (
            <div className="empty-state">No pending coach approvals.</div>
          ) : (
            <div className="approval-list">
              {pendingCoaches.map(coach => (
                <div key={coach.id} className="approval-card">
                  <div>
                    <div className="approval-name">{coach.name}</div>
                    <div className="approval-email">{coach.email}</div>
                    <div className="approval-date">Applied: {new Date(coach.created_at).toLocaleDateString()}</div>
                  </div>
                  <button
                    className="btn-approve"
                    disabled={actionLoading === coach.id + '_approve'}
                    onClick={() => approveCoach(coach.id)}
                  >
                    {actionLoading === coach.id + '_approve' ? '...' : 'Approve'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── ALL USERS ── */}
      {activeSection === 'users' && (
        <>
          <div className="um-filters">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="um-search"
            />
            <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="um-role-filter">
              <option value="all">All Roles</option>
              {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>

          {error && <div className="error-message">{error}</div>}
          {loading ? (
            <div className="loading-state">Loading users...</div>
          ) : (
            <div className="users-table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(u => (
                    <tr key={u.id} className={!u.is_active ? 'row-inactive' : ''}>
                      <td className="td-name">{u.name}</td>
                      <td className="td-email">{u.email}</td>
                      <td>
                        {u.id === currentUser?.id ? (
                          <span className="role-badge" style={{ background: ROLE_COLORS[u.role] || '#555' }}>
                            {u.role} (you)
                          </span>
                        ) : (
                          <select
                            className="role-select"
                            value={u.role}
                            disabled={actionLoading === u.id + '_role'}
                            onChange={e => changeRole(u.id, e.target.value)}
                          >
                            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        )}
                      </td>
                      <td>
                        <span className={`status-dot ${u.is_active ? 'active' : 'inactive'}`}>
                          {u.is_active ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td>
                        {u.id !== currentUser?.id && (
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <button
                              className={`btn-toggle ${u.is_active ? 'btn-suspend' : 'btn-activate'}`}
                              disabled={actionLoading === u.id + '_active'}
                              onClick={() => toggleActive(u.id, u.is_active)}
                            >
                              {actionLoading === u.id + '_active' ? '...' : (u.is_active ? 'Suspend' : 'Reactivate')}
                            </button>
                            <button
                              className="btn-toggle"
                              style={{ background: '#4f46e5', color: '#fff' }}
                              disabled={actionLoading === u.id + '_pw'}
                              onClick={() => resetPassword(u.id, u.name)}
                              title="Set a new temporary password for this user"
                            >
                              {actionLoading === u.id + '_pw' ? '...' : 'Reset PW'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredUsers.length === 0 && <div className="empty-state">No users match your filters.</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default UserManagement;
